import { Typography } from '@mui/material'
import React, { memo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tw from 'twin.macro'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { UploadMVR } from 'src/constants/actionTypes'
import MVRIcon from 'src/assets/icons/bgc.svg'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { Formik } from 'formik'
import { MVRManualUploadFormSchema } from './MVRManualUploadForm.validator'
import { FormikFileInput } from '../shared/form/formik/FormikFileInput'
import { MVRManualUploadFormValues } from './MVRManualUploadForm.interface'
import { FormErrorMessage } from '../shared/form/FormErrorMessage'
import { MVRCollectDataStep } from 'src/interfaces/collectData'
import { CollectDataStepIndex } from '../client/CollectDataStepIndex'
import { MVRProceedWithYardstik } from './MVRProceedWithYardstik'
import { MVRValidRules } from './shared/MVRValidRules'
import { Button } from '../shared/Button'

interface Props {
  hideStepsCounter?: boolean
  isYardstikEnabled?: boolean
}

const initialValues: MVRManualUploadFormValues = {
  mvrFile: undefined,
}

const MVRManualUploadComponent = ({ hideStepsCounter, isYardstikEnabled }: Props) => {
  const dispatch = useDispatch()

  const currentStep = useSelector(getCurrentCollectDataStep) as MVRCollectDataStep

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const handleFormSubmit = async (values: MVRManualUploadFormValues) => {
    setIsLoading(true)
    setError(null)

    const requestBody = {
      stepId: currentStep.id,
      file: values.mvrFile,
    }

    try {
      await createAsyncAction(dispatch, UploadMVR.request(requestBody))
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validationSchema={MVRManualUploadFormSchema}
    >
      {({ handleSubmit }) => (
        <div css={tw`h-full w-full flex flex-col justify-between`}>
          <div>
            <div
              css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
            >
              <img src={MVRIcon} />
            </div>
            {!hideStepsCounter && <CollectDataStepIndex stepId={currentStep.id} />}
            <Typography css={tw`mb-6 mt-2`} variant="h2">
              Upload Motor Vehicle Record
            </Typography>
            <Typography css={tw`mb-4 text-shades-400`}>
              Once you submit the documents, you will automatically advance to the next step.
            </Typography>
            {isYardstikEnabled && <MVRProceedWithYardstik />}
            <Typography css={tw`my-2 text-shades-400`} variant="overline">
              upload motor vehicle record
            </Typography>
            <div css={tw`bg-shades-0 p-4 rounded-2xl mb-8`}>
              <MVRValidRules />
              <Typography css={tw`mb-1.5 text-shades-400`} variant="overline">
                Upload Motor Vehicle Record Report File
              </Typography>
              <FormikFileInput name="mvrFile" />
            </div>
          </div>
          <FormErrorMessage css={tw`mb-4`} error={error} />
          <Button
            className={tw`w-full`}
            loading={isLoading}
            onClick={() => {
              handleSubmit()
            }}
          >
            Submit and Continue
          </Button>
        </div>
      )}
    </Formik>
  )
}

export const MVRManualUpload = memo(MVRManualUploadComponent)
